import * as Turbo from '@hotwired/turbo'

/**
 * Notices when anything is written, so the rest of the app can stop showing
 * what was true before.
 *
 * **The bug this exists for.** Every list in the app is server-rendered and
 * read once, as JSON on a `data-` attribute, when its React island mounts (see
 * lib/turboMount.tsx). Turbo keeps a snapshot of each page it leaves, and a
 * restoration visit — the back button — renders that snapshot instead of
 * asking the server again. So: open a project from the list, change its dates,
 * press back, and the row still shows what it showed before. Verified
 * 2026-09-14 on `/projects`, where the row kept both the old date range *and*
 * the old status, since status is derived from the dates.
 *
 * Nothing was wrong with the write, which is what makes it hard to see. The
 * save succeeded, the record page was right, the database was right, and the
 * only wrong thing on screen was a picture of a page taken before any of it
 * happened.
 *
 * **Why it is done here rather than at each call site.** Writes leave from
 * everywhere — `writeSetting`, `submitJson`, and a couple of dozen components
 * that call `fetch` directly. Asking each of them to remember is the
 * arrangement that produced this bug, and it decays again the first time
 * somebody adds a screen. One rule, applied to the thing every write has in
 * common, cannot be forgotten by new code.
 *
 * The cost is a re-fetch on the next navigation after any write, which is the
 * correct trade: a cached page is a speed-up, and a *wrong* cached page is not
 * a speed-up at all. Reads are untouched, so ordinary browsing still comes off
 * the cache.
 *
 * **The other half** is a list that is already on screen when something on it
 * changes — the record sheet saving over the list it was opened from, where
 * there is no navigation to be stale, just a page that was drawn once. Turbo's
 * cache is no help there, so this also announces the write: `onWrite` is what
 * lib/turboMount.tsx listens to in order to re-read a mounted island's props
 * from the server.
 */
const listeners = new Set<() => void>()

/**
 * Called after every successful write, wherever it left from. Returns the
 * function that stops listening — a mounted island unsubscribes when it goes,
 * or a page swapped by Turbo would keep refreshing a root nobody can see.
 */
export function onWrite(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function initWriteWatch() {
  const original = window.fetch

  window.fetch = async function (...args: Parameters<typeof fetch>) {
    const response = await original.apply(this, args)

    if (writes(args[0], args[1]) && response.ok) {
      Turbo.cache.clear()
      // A listener that throws is a bug in that listener, and it must not stop
      // the others hearing about the write or the write's own caller getting
      // its response back.
      listeners.forEach((listener) => {
        try {
          listener()
        } catch {
          /* deliberately ignored */
        }
      })
    }

    return response
  }
}

/**
 * A write is any method that is not a read. Taken from the request the caller
 * actually made — `Request` objects carry their own method, and most of this
 * app passes a string URL and an init instead, so both shapes are asked.
 *
 * Rails is POSTed to with `_method` for patch and delete, so POST covers far
 * more than creation here and there is no point trying to be cleverer about
 * which verbs change something.
 */
function writes(input: RequestInfo | URL, init?: RequestInit): boolean {
  const method = init?.method ?? (input instanceof Request ? input.method : 'GET')
  return !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())
}
